<script setup lang="ts">
import { computed, ref } from 'vue';
import { useForm } from 'vee-validate';
import { AppTypeDentalOrVision, DentalAndVisionApplication } from '@/application/shared/domain/DentalAndVisionApplication';
import { useDentalAndVisionApplication } from '@/shared/infrastructure/composition/useDentalAndVisionApplication';
import BaseSection from '@/application/shared/infrastructure/components/BaseSection.vue';
import QuestionLegalAgreement from '@/application/shared/infrastructure/components/questions/QuestionLegalAgreement.vue';
import { PrimaryButton, ModalDialog } from '@hfc/ui-core';
import { useUrlHelper } from '@/shared/infrastructure/composition/useUrlHelper';

const { handleSubmit } = useForm();
const runValidations = handleSubmit(() => true);

const { getBaseUrl } = useUrlHelper();

const props = defineProps<{
  applicationType: AppTypeDentalOrVision;
}>();

interface EmitInterface {
  (event: 'nextSection'): void
}

const emits = defineEmits<EmitInterface>();

const showTermsModal = ref<boolean>(false);
const legalAgreementRef = ref();

const {
  dentalAndVisionApplicationStore,
  application
} = useDentalAndVisionApplication(props.applicationType);

const sectionForm = computed(() => {
  const agree_terms_and_conditions = application.value.agree_terms_and_conditions;
  const agree_consent = application.value.agree_consent;

  return {
    agree_consent,
    agree_terms_and_conditions
  };
});

const setValue = (value: Array<string | null> | Partial<DentalAndVisionApplication>): void => {
  if (Array.isArray(value)) {
    const response = { agree_terms_and_conditions: value.includes('terms'), agree_consent: value.includes('consent') };
    dentalAndVisionApplicationStore.updateApplication(response, props.applicationType);
  }
};

const sectionSubmit = async (): Promise<boolean | undefined> => {
  return await runValidations();
};

const confirmMethod = async (): Promise<void> => {
  await legalAgreementRef.value.acceptAll();
  showTermsModal.value = false;
  emits('nextSection');
};

defineExpose({
  sectionSubmit: sectionSubmit
});
</script>
<template>
  <BaseSection class="bg-white px-4 py-2" v-highlight="{ multipleCoincidences: true }">
    <div class="py-4 px-6 rounded-sm mb-4 overflow-y-scroll max-h-[calc(100vh-450px)] h-full text-justify">
      <h3>Terms and Conditions</h3>
      <br>
      <p>In order to enroll on this website, you must agree to the <span class="cursor-pointer text-blue-400" @click="showTermsModal = true"><b>Terms and
      Conditions</b></span> and Privacy Policy.</p>
      <br>
      <h3>Consent</h3>
      <br>
      <p>I agree to receive text messages and calls about my health care coverage from
      Health For California Insurance Center.</p>
      <br>
      <p>I consent to receive autodialed and pre-recorded calls and/or text/SMS
      messages at the telephone number I provided (including my cell phone number),
      from or on behalf of Health For California Insurance Center.</p>
      <br>
      <p>I understand that message and data rates may apply.</p>
      <br>
      <p>By continuing you acknowledge that Health for California Insurance Center will
      have access to the personally identifiable information that you must provide on
      the following application to complete the eligibility and enrollment process. If
      you do not authorize Health for California Insurance Center to have access to
      the personally identifiable information that you must provide on the following
      application, click “Previous” and do not proceed with the application. For
      additional information regarding enrollment with Blue Shield, please call
      <a href="tel:1-877-752-4737"><b>1-877-752-4737</b></a>.</p>
      <br>
      <p>We are required by law to maintain the privacy and security of your personal
      information. We will let you know promptly if a breach occurs that may have
      compromised the privacy or security of your information. We must follow the
      duties and privacy practices described in this notice and give you a copy of it.
      We will not use or share your information other than as described here unless
      you tell us we can in writing. If you tell us we can, you may change your mind at
      any time. Let us know in writing if you change your mind. Health For California
      Insurance Center and Blue Shield utilizes secure electronic interfaces when
      sharing personally identifiable information electronically. We will protect
      personally identifiable information with operational, administrative, technical,
      and physical safeguards to ensure its confidentiality, integrity, and availability
      and to prevent unauthorized or inappropriate access, use, or disclosure.</p>
    </div>
    <QuestionLegalAgreement :value="sectionForm" @update:value="setValue" ref="legalAgreementRef"/>
  </BaseSection>
  <ModalDialog v-if="showTermsModal == true" confirm-button="I agree" size="xl" @close="showTermsModal = false" @confirm="confirmMethod" :confirm-button-type="PrimaryButton">
    <template #content>
      <iframe class="w-full h-screen" :src="getBaseUrl('/account-terms-and-conditions')"></iframe>
    </template>
  </ModalDialog>
</template>
